import type { BaseForm as BaseFormType } from '#components'
import type { ContentProductEspots } from '#root/api/clients/content/data-contracts'
import type { InventoryData, ProductAttributesOptionsColor, ProductPrice, ProductReviewsQuestionsResponse, ProductReviewsRollupProperty } from '#root/api/clients/product/data-contracts'
import type { Props as PickupInStoreProps } from '#types/components/product/pickupInStore'
import type { ProductQuestionsParams, ProductReviewsData, ProductReviewsParams } from '#types/components/product/reviews'
import type { InteractionOrigin, ProductContext } from '#types/gtm'
import type { ImageItem, VideoItem } from '#types/media'
import type { BaseNotification } from '#types/notification'
import type { AttributeSelection, ProductAttribute, ProductAttributeType, ProductExtended } from '#types/product'
import type { InjectionKey } from 'vue'

interface PdpContext {
  productId: Ref<string>
  pending: Ref<boolean>
  error: Ref<boolean>
  product: Ref<ProductExtended | null>
  productPending: Ref<boolean>
  productError: Ref<Error | null>
  inventory: Ref<InventoryData | null>
  inventoryError: Ref<Error | null>
  productPrice: Ref<ProductPrice | undefined>
  isSelectedProductOutOfStock: Ref<boolean | undefined>
  isSomeVariantNotifyMe: Ref<boolean | undefined>
  isComingSoon: Ref<boolean>
  isOutOfStock: Ref<boolean>
  isRobisOnly: Ref<boolean | undefined>
  showSizeAndFit: Ref<boolean>
  dialogNotification: BaseNotification
  galleryItems: Ref<(ImageItem | VideoItem)[]>
  reviewsData: Ref<ProductReviewsData | undefined>
  reviewsPending: Ref<boolean>
  reviewsError: Ref<Error | null>
  reviewsQuery: ProductReviewsParams
  questionsData: Ref<ProductReviewsQuestionsResponse | null>
  questionsPending: Ref<boolean>
  questionsError: Ref<Error | null>
  questionsQuery: ProductQuestionsParams
  attributeSelection: AttributeSelection
  fitRating: Ref<ProductReviewsRollupProperty | undefined>
  addToCartLoading: Ref<boolean>
  checkoutCtaLoading: Ref<boolean>
  isProductOrInventoryPending: ComputedRef<boolean>
  isFavorite: Ref<boolean>
  isFavoritePending: Ref<boolean>
  isNotifyMe: ComputedRef<boolean | undefined>
  isSignInToBuy: Ref<boolean>
  isNonLoyaltyMember: Ref<boolean>
  selectedVariant: Ref<any>
  shippingMethod: Ref<'standard' | 'pickup' | undefined>
  productColor: Ref<ProductAttributesOptionsColor | null>
  attributeSelectionLabels: Ref<any>
  attributeSelectionFormRef: Ref<InstanceType<typeof BaseFormType> | null>
  eSpots: ComputedRef<ContentProductEspots['sectionsMap'] | undefined>
  eyebrowStyle: Ref<any>
  nextAttribute: Ref<ProductAttribute | undefined>
  resetNotification: () => void
  onZoom: (index: number, zoomFactor: number) => void
  selectColor: (_id: any, url: any, colorLabel?: string) => Promise<void>
  selectSize: (type: any, value: any, label?: string) => void
  openSizeGuide: (payload: any) => void
  addToCart: (payload?: any) => Promise<boolean>
  addToCartAndCheckout: (payload: any) => Promise<void>
  findInStore: (attributeSelectionLabels: Record<string, { label: string, value: string }>, mode?: PickupInStoreProps['mode']) => Promise<void>
  setNotification: (payload: any) => void
  toggleFavorites: (productColor?: string) => Promise<void>
  triggerApplePaySession: () => void
  tryItOn: () => unknown
  noVariantSelected: () => boolean
  openQuickshop: (payload: any, gtmConfig?: ProductContext & { appSource?: InteractionOrigin }) => Promise<any>
  openShopTheLook: (payload: any) => void
  getAttrOptionAvailable: (type: ProductAttributeType, value: string) => boolean | undefined
  getAttrOptionNotifyMe: (type: ProductAttributeType, value: string) => boolean | undefined
}

export const PdpContextKey: InjectionKey<PdpContext> = Symbol('PdpContext')
